import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useGetTransactionItemDetail } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { ComponentType } from '@/features/transaction_items/List/type';
import {
  TransactionItemGeneralForm,
  Props as TransactionItemGeneralFormProps,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { Loading } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, Suspense, memo, useEffect, useMemo } from 'react';
import styles from './DetailDrawer.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  selectedDetailId: string;
  setIsDetailLoading: (isLoading: boolean) => void;
} & TransactionItemGeneralFormProps;

const castNumericValue = (value: string | number | undefined | null) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  // Value will come from the API as a decimal string.
  // Integers will have a trailing ".0" that needs to be removed
  return String(value).split('.')[0];
};

const toNumberOrDefault = (
  value: string | number | undefined,
  defaultValue: number
) => {
  if (value === undefined) return defaultValue;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const InnerDetailDrawer: FC<Props> = memo(
  ({ selectedDetailId, setIsDetailLoading, ...props }) => {
    const { data } = useGetTransactionItemDetail(selectedDetailId);

    const castedDataToTransactionItemFormType = useMemo(() => {
      return {
        label: data?.label ?? '',
        is_visible: data?.is_visible ?? false,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        component_type: data?.component_type as ComponentType,
        max_chars: toNumberOrDefault(data?.max_chars, 0),
        min_chars: toNumberOrDefault(data?.min_chars, 0),
        min_value: castNumericValue(data?.min_value),
        max_value: castNumericValue(data?.max_value),
        allow_decimal: !!data?.decimal_places,
        placeholder: data?.placeholder ?? '',
        tooltip_message: data?.tooltip_message ?? '',
        date_format_type: data?.date_format_type,
      };
    }, [data]);

    useEffect(() => {
      setIsDetailLoading(false);
    }, [setIsDetailLoading]);

    return (
      <TransactionItemGeneralForm
        {...props}
        selectedDetailId={selectedDetailId}
        defaultValues={castedDataToTransactionItemFormType}
      />
    );
  }
);
InnerDetailDrawer.displayName = 'InnerDetailDrawer';

const LoadingComponent: FC<Props> = memo(({ setIsDetailLoading }) => {
  useEffect(() => {
    setIsDetailLoading(true);
  }, [setIsDetailLoading]);
  return (
    <div className={cx(styles['loading-container'])}>
      <Loading size='large' />
    </div>
  );
});
LoadingComponent.displayName = 'LoadingComponent';

export const DetailDrawer: FC<Props> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<LoadingComponent {...props} />}>
        <InnerDetailDrawer {...props} />
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
DetailDrawer.displayName = 'DetailDrawer';
